import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  DateRangePicker,
  DownloadButton,
  GroupedTable,
  LoadingComponent,
  Modal,
  MultiSelect,
  NoDataFoundTemplate,
  Select,
  Table,
} from "../../../components/core";
import { gql, split, useLazyQuery, useQuery } from "@apollo/client";
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from "recharts";
import TabComponent from "../../../components/core/tab";
import { ContentContainer } from "../../../components/layout";
import CustomTooltip from "./custom-tooltip";
import {
  QUERY_CHART_DATA,
  QUERY_COMPANY_BRANCHES,
  QUERY_COMPANY_NKCNAME,
  QUERY_LOCKER_NAME,
  QUERY_LOCKER_TYPE,
  QUERY_PARTNER_BRANCH_PERFORMANCE_BY_DAY,
  QUERY_PARTNER_PERFORMANCE_REPORTS,
} from "../../../constants/graphql/parter-perf-graphql";
import ParterGroupedTable from "./partnerPerf-table";
import PrimereactDateRange from "../../../components/core/date-range-primereact";
import { formatDateYYYYMMDD } from "../../../algorithms/date";
import { maxDate } from "../drop-off-status";
import { NEWEST_LOCKER_NAME_QUERY } from "../../../features/stay-time-in-locker/stay-time-query/stay-time-graphql";
import { Card } from "primereact/card";
import { Accordion, AccordionTab } from "primereact/accordion";

const PartnerPerformanceReports = () => {
  // Local Variables
  const operatorId = JSON.parse(localStorage.getItem("user")).operator_id;
  const dt = useRef(null);

  //local setters
  const [partnerPerfromanceReports, setPartnerPerformnaceReports] = useState(
    []
  );
  const [lockers, setLockers] = useState([]);
  const [branchMapping, setBranchMapping] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [searchResultHeader, setSearchResultHeader] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [isTableEmpty, setIsTableEmpty] = useState(false);
  const [filterVariables, setFilterVariables] = useState({
    lockerType: "-1",
    selectedLockerName: [],
    startDate: maxDate,
    endDate: new Date(),
    selectedStatus: "-1",
    selectedPartnerCompany: "-1",
    selectedPartnerBranch: [],
    selectedRadio: "weekday",
  });
  let colors = [
    "rgb(125, 215, 230)",
    "rgb(255, 150, 75)",
    "rgb(180, 60, 200)",
    "rgb(90, 190, 50)",
    "rgb(255, 100, 180)",
    "rgb(70, 120, 255)",
    "rgb(200, 180, 20)",
    "rgb(150, 75, 0)",
    "rgb(40, 170, 120)",
    "rgb(255, 200, 0)",
    "rgb(110, 45, 160)",
    "rgb(0, 255, 100)",
    "rgb(255, 50, 50)",
    "rgb(30, 80, 220)",
    "rgb(210, 160, 20)",
    "rgb(120, 0, 140)",
    "rgb(0, 200, 150)",
    "rgb(255, 80, 0)",
    "rgb(80, 160, 60)",
    "rgb(170, 30, 90)",
  ];

  //search Handler variables
  const [noResultSearch, setNoResultSearch] = useState(false);
  const [noDateSelected, setNoDateSelected] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [branchForChart, setBranchForChart] = useState([]);

  const [branchChartData, setBranchChartData] = useState([]);
  const [lockerForChart, setLockerForChart] = useState([]);

  const [packageForChart, setPackageForChart] = useState([]);
  // const [selectedRadio, setSelectedRadio] = useState("weekday");
  const [tempLockers, setTempLockers] = useState([]);

  //<SELECT> Data handler variables
  const [lockerName, setLockerName] = useState([]);
  const [partnerCompany, setPartnerCompany] = useState([]);
  const [partnerCompanyForChart, setPartnerCompanyForChart] = useState([]);
  const [partnerBranchForChart, setPartnerBranchForChart] = useState([]);

  const [partnerBranch, setPartnerBranch] = useState([]);
  const [lockerType, setLockerType] = useState([]);

  //<SELECT> Setter Variable
  const [selectedLockerName, setSelectedLockerName] = useState("-1");
  const [selectedPartnerCompany, setSelectedPartnerCompany] = useState("-1");
  const [selectedPartnerBranch, setSelectedPartnerBranch] = useState("-1");
  const [selectedLockerType, setSelectedLockerType] = useState("-1");
  const [allLockersSelected, setAllLockersSelected] = useState(false);
  const [allBranchesSelected, setAllBranchesSelected] = useState(false);

  const [validation, setValidation] = useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });
  //To display search result in header
  function handleSearchResultHeader() {
    const lockerTypeName =
      filterVariables.lockerType === "-1"
        ? "ALL Locker types"
        : filterVariables.lockerType === "1"
        ? "Parcel Locker"
        : "Fridge Locker";

    let lockerNameText;
    if (allLockersSelected) {
      lockerNameText = "ALL Lockers";
    } else {
      const lockerLabels = filterVariables.selectedLockerName
        .map((selectedLocker) => {
          const matchedLocker = lockerName.find(
            (item) => parseInt(item.value) === parseInt(selectedLocker.value)
          );
          return matchedLocker ? matchedLocker.label : null;
        })
        .filter((label) => label !== null);

      lockerNameText =
        lockerLabels.length > 0 ? lockerLabels.join(", ") : "ALL Lockers";
    }

    const partnerCompanyName =
      filterVariables.selectedPartnerCompany === "-1"
        ? "All Partners"
        : partnerCompany.find(
            (item) => item.value === filterVariables.selectedPartnerCompany
          )?.name;

    let partnerBranchText;
    if (allBranchesSelected) {
      lockerNameText = "ALL Branches";
    } else {
      const branchLabels = filterVariables.selectedPartnerBranch
        .map((sB) => {
          const matchedBranch = partnerBranch.find(
            (item) => parseInt(item.value) === parseInt(sB.value)
          );
          return matchedBranch ? matchedBranch.label : null;
        })
        .filter((label) => label !== null);

      partnerBranchText =
        branchLabels.length > 0 ? branchLabels.join(", ") : "ALL Branches";
    }

    setSearchResultHeader(`for
      ${lockerTypeName},
      ${lockerNameText},
      ${partnerCompanyName},
      ${partnerBranchText},
	  from ${formatDate(filterVariables.startDate)} to ${formatDate(
      filterVariables.endDate
    )}
    `);
  }
  const handleFilterChange = (e, field) => {
    let value = e.target.value;
    if (field === "startDate" || field === "endDate") {
      value = value ? new Date(value) : null;
    }

    // If changing partner company, also clear partner branch
    if (field === "selectedPartnerCompany") {
      setFilterVariables({
        ...filterVariables,
        [field]: value,
        selectedPartnerBranch: [], // Clear partner branch when company changes
      });
    } else if (field === "lockerType") {
      setFilterVariables({
        ...filterVariables,
        [field]: value,
        selectedLockerName: [], // Clear partner branch when company changes
      });
    } else {
      setFilterVariables({
        ...filterVariables,
        [field]: value,
      });
    }
  };

  function formatDate(inputDate) {
    if (inputDate == null) {
      return "";
    }
    const date = new Date(inputDate);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString(undefined, options);
  }

  // Graphql
  const [QUERYpartnerPerformance, { data, loading }] = useLazyQuery(
    QUERY_PARTNER_PERFORMANCE_REPORTS,
    { fetchPolicy: "no-cache" }
  );
  //   const partnerLockerName = useQuery(QUERY_LOCKER_NAME, {
  //     variables: {
  //       operatorId: operatorId,
  //       selectedLockerType: selectedLockerType,
  //     },
  //   });
  const fetchCompanyName = useQuery(QUERY_COMPANY_NKCNAME, {
    variables: {
      operatorId: operatorId,
    },
  });
  const [fetchCompanyBranch, { data: companyBranchData }] = useLazyQuery(
    QUERY_COMPANY_BRANCHES,
    {
      variables: {
        operatorId: operatorId,
      },
    }
  );
  const fetchLockerType = useQuery(QUERY_LOCKER_TYPE);
  const [fetchChartData, resultFetchChartData] = useLazyQuery(
    QUERY_CHART_DATA,
    {
      fetchPolicy: "no-cache",
    }
  );
  const [fetchChartDataByBranch, resultFetchChartDataByBranch] = useLazyQuery(
    QUERY_PARTNER_BRANCH_PERFORMANCE_BY_DAY,
    {
      fetchPolicy: "no-cache",
    }
  );

  const [fetchLockerName, { data: lockerNameData }] = useLazyQuery(
    NEWEST_LOCKER_NAME_QUERY,
    {
      variables: {
        operatorId: operatorId,
        //   selectedLockerType: selectedLockerType,
      },
    }
  );

  //Table data
  useEffect(() => {
    if (data) {
      let temp = [];
      let temp2 = [];
      let temp3 = [];
      let temp4 = [];
      let temp7 = [];

      // Group data by partner first to aggregate properly
      const partnerGroups = {};
      const branchGroups = {};

      data.partnerPerformanceByLocker.forEach((k, i) => {
        let obj = { ...k };
        let tempArr = [];
        let tempArr2 = [];
        var total = 0;

        obj.lockers.map((k1, i1) => {
          tempArr.push(k1.name);
          tempArr2.push(k1.packages);
          total += k1.packages;
        });

        obj.locker = tempArr;
        obj.packages = tempArr2;
        obj.total = total;
        temp.push(obj);

        // Group by partner name for aggregation
        if (!partnerGroups[obj.partnerName]) {
          partnerGroups[obj.partnerName] = {
            totalPackages: 0,
            totalLockers: 0,
            lockerNames: [],
          };
        }

        partnerGroups[obj.partnerName].totalPackages += total;
        partnerGroups[obj.partnerName].totalLockers += tempArr.length;
        partnerGroups[obj.partnerName].lockerNames = [
          ...partnerGroups[obj.partnerName].lockerNames,
          ...tempArr,
        ];

        // Group by branch for branch chart
        const branchKey = `${obj.partnerName} - Branch ${obj.partnerBranchId}`;
        if (!branchGroups[branchKey]) {
          branchGroups[branchKey] = 0;
        }
        branchGroups[branchKey] += total;

        temp4.push(total);
      });

      // Create combined objects from grouped data
      Object.keys(partnerGroups).forEach((partnerName) => {
        const group = partnerGroups[partnerName];
        temp2.push({ [partnerName]: group.totalPackages });
        temp3.push({ [partnerName]: group.totalLockers });
      });

      // Create branch chart data
      Object.keys(branchGroups).forEach((branchKey) => {
        temp7.push({ [branchKey]: branchGroups[branchKey] });
      });

      // Get unique locker names for the selected partner
      const allLockerNames = Object.values(partnerGroups).flatMap(
        (group) => group.lockerNames
      );
      const uniqueLockerNames = [...new Set(allLockerNames)];
      setTempLockers(uniqueLockerNames);

      setTotalCol(temp4);

      const combinedObject0 = temp3.reduce(
        (acc, obj) => Object.assign(acc, obj),
        {}
      );
      const combinedObject1 = temp2.reduce(
        (acc, obj) => Object.assign(acc, obj),
        {}
      );
      // const combinedObject2 = temp7.reduce(
      //   (acc, obj) => Object.assign(acc, obj),
      //   {}
      // );

      setLockerForChart(combinedObject0);
      setPackageForChart(combinedObject1);
      // setBranchForChart(combinedObject2);

      setPartnerPerformnaceReports(temp);
      setIsLoading(false);

      // console.log("Partner Groups:", partnerGroups);
      // console.log("Branch Groups:", branchGroups);
      // console.log("Combined Objects:", {
      //   lockers: combinedObject0,
      //   packages: combinedObject1,
      //   branches: combinedObject2,
      // });

      if (temp.length === 0) {
        setIsLoading(false);
        setIsTableEmpty(true);
      }
    }
  }, [data]);

  useEffect(() => {
    if (resultFetchChartData.data) {
      let tempData = [];
      let tempPartners = [];
      resultFetchChartData.data.partnerPerformanceByDay.forEach(
        (element, index) => {
          let tempArr = [];
          element.partners.forEach((e, i) => {
            if (e.packages != null) {
              tempArr.push({ [e.name]: e.packages });
              if (!tempPartners.includes(e.name)) {
                tempPartners.push(e.name);
              }
            }
          });
          tempArr.push({ date: element.date });
          const combinedObject = tempArr.reduce(
            (acc, obj) => ({ ...acc, ...obj }),
            {}
          );
          tempData.push(combinedObject);
        }
      );
      let tempBars = [];
      // setTempPartners1(tempPartners);
      tempPartners.forEach((e, i) => {
        tempBars.push(
          <Bar
            dataKey={e}
            fill={colors[i % 20]}
            maxBarSizeSize={50}
            label={e}
          />
        );
      });
      setPartnerCompanyForChart(tempBars);
      setChartData(tempData);
      // console.log(tempData);
    }
  }, [resultFetchChartData.data]);

  // new filtering / chart by partner branch
  useEffect(() => {
    if (resultFetchChartDataByBranch.data) {
      console.log({ DATA: resultFetchChartDataByBranch.data });
      let tempData = [];
      let tempPartners = [];
      let branchForChartTemp = {};

      // First, collect all unique branches
      resultFetchChartDataByBranch.data.partnerPerformanceByDayWithBranches.forEach(
        (element) => {
          element.partnerBranches.forEach((branch) => {
            if (branch.packages != null) {
              const branchKey = `${branch.partnerName} - Branch ${branch.partnerBranch}`;
              if (!tempPartners.includes(branchKey)) {
                tempPartners.push(branchKey);
                // Initialize total packages for this branch
                branchForChartTemp[branchKey] = 0;
              }
            }
          });
        }
      );

      // Process each day
      resultFetchChartDataByBranch.data.partnerPerformanceByDayWithBranches.forEach(
        (element) => {
          let dayData = { date: element.date };

          // Initialize all branches with 0 for this day
          tempPartners.forEach((branchKey) => {
            dayData[branchKey] = 0;
          });

          // Fill in actual data for branches that have packages
          element.partnerBranches.forEach((branch) => {
            if (branch.packages != null) {
              // find name by mapping partnerBranch
              const branchKey = `${branch.partnerName} - Branch ${branch.partnerBranch}`;
              dayData[branchKey] = branch.packages;
              // Add to total for branchForChart
              branchForChartTemp[branchKey] += branch.packages;
            }
          });

          tempData.push(dayData);
        }
      );

      // Create bars for each branch
      let tempBars = [];
      tempPartners.forEach((branchKey, i) => {
        tempBars.push(
          <Bar
            key={i}
            dataKey={branchKey}
            // comment out stackId if u wanna unstack
            stackId="a"
            fill={`hsl(${(i * 360) / tempPartners.length}, 70%, 50%)`}
          />
        );
      });

      setPartnerBranchForChart(tempBars);
      setBranchChartData(tempData);
      setBranchForChart(branchForChartTemp);
    }
  }, [resultFetchChartDataByBranch.data]);

  //To Fetch Locker Type
  useEffect(() => {
    if (fetchLockerType.data) {
      let tempData = [];
      fetchLockerType.data.lockerTypes.forEach((element, index) => {
        tempData.push({
          value: element.lockerTypeId,
          name: element.lockerType,
        });
      });
      setLockerType(tempData);
    }
  }, [fetchLockerType.data]);

  useEffect(() => {
    if (filterVariables.lockerType !== "-1") {
      fetchLockerName({
        variables: {
          operatorId: operatorId,
          lockerTypeId: filterVariables.lockerType,
        },
      });
    }
  }, [filterVariables.lockerType, operatorId]);
  useEffect(() => {
    if (lockerNameData) {
      const tempData = lockerNameData.lockerInfo.map((element) => ({
        label: element.lockerName,
        value: element.lockerId,
      }));

      if (!allLockersSelected) {
        tempData.unshift({
          value: 0,
          label: "All Lockers",
        });
      }

      setLockerName(tempData);
    }
  }, [lockerNameData, allLockersSelected]);

  useEffect(() => {
    const selected = filterVariables.selectedLockerName;
    const allLockers = lockerName;

    const hasZeroValue = selected.some((item) => item.value === 0);

    if (hasZeroValue) {
      setAllLockersSelected(true);
    } else if (selected.length === 0) {
      setAllLockersSelected(false);
    } else if (selected.length !== allLockers.length) {
      setAllLockersSelected(false);
    } else {
      setAllLockersSelected(true);
    }
  }, [filterVariables.selectedLockerName, lockerName]);
  useEffect(() => {
    if (allLockersSelected) {
      const selectedLockers = lockerName.filter((locker) => locker.value !== 0);

      setFilterVariables({
        ...filterVariables,
        selectedLockerName: selectedLockers,
      });
    }
  }, [allLockersSelected, lockerName]);
  //To Fetch Company Branches
  useEffect(() => {
    if (filterVariables.selectedPartnerCompany !== "-1") {
      fetchCompanyBranch({
        variables: {
          operatorId: operatorId,
          selectedPartnerCompany: filterVariables.selectedPartnerCompany,
        },
      });
    }
  }, [filterVariables.selectedPartnerCompany, operatorId]);

  useEffect(() => {
    if (companyBranchData && companyBranchData.searchBranches) {
      const tempData = companyBranchData.searchBranches.map((element) => ({
        value: element.partnerBranchId,
        label: element.partnerBranch,
      }));

      // Add "All Branches" option
      if (!allBranchesSelected) {
        tempData.unshift({
          value: 0,
          label: "All Partner Branches",
        });
      }
      setPartnerBranch(tempData);
    }
  }, [companyBranchData, allBranchesSelected]);
  useEffect(() => {
    const selected = filterVariables.selectedPartnerBranch;
    const allBranches = partnerBranch;

    const hasZeroValue = selected.some((item) => item.value === 0);

    if (hasZeroValue) {
      setAllBranchesSelected(true);
    } else if (selected.length === 0) {
      setAllBranchesSelected(false);
    } else if (selected.length !== allBranches.length) {
      setAllBranchesSelected(false);
    } else {
      setAllBranchesSelected(true);
    }
  }, [filterVariables.selectedPartnerBranch, partnerBranch]);

  useEffect(() => {
    if (allBranchesSelected) {
      const branches = partnerBranch.filter((branch) => branch.value !== 0);

      setFilterVariables({
        ...filterVariables,
        selectedPartnerBranch: branches,
      });
    }
  }, [allBranchesSelected, partnerBranch]);

  //To Fetch Company Name
  useEffect(() => {
    if (fetchCompanyName.data) {
      let tempData = [];
      fetchCompanyName.data.partners.forEach((element, index) => {
        tempData.push({
          value: element.partnerId,
          name: element.companyNickname,
        });
      });
      setPartnerCompany(tempData);
    }
  }, [fetchCompanyName.data]);

  // table Query Variables
  const buildVariables = (forCharData = false) => {
    const start = formatDateYYYYMMDD(filterVariables.startDate);
    const end = formatDateYYYYMMDD(filterVariables.endDate);

    const updatedVariables = {
      operatorId: operatorId,
      dateStart: start,
      dateEnd: end,
    };

    // append selectedradio only for chart data
    if (forCharData) {
      updatedVariables.selectedRadio = filterVariables.selectedRadio;
    }

    if (filterVariables.selectedPartnerCompany !== "-1") {
      updatedVariables.selectedPartnerCompany =
        filterVariables.selectedPartnerCompany;
    }

    if (
      filterVariables.selectedLockerName.length !== 0 &&
      filterVariables.lockerType !== "-1"
    ) {
      //   if (selectedLockerName != "200") {
      const mappedLockerNames = filterVariables.selectedLockerName.map(
        (locker) => locker.value
      );

      updatedVariables.selectedLockerName = mappedLockerNames;
      //   }
    }

    if (filterVariables.selectedPartnerBranch.length !== 0) {
      const branchVal = filterVariables.selectedPartnerBranch.map(
        (locker) => locker.value
      );

      updatedVariables.selectedPartnerBranch = branchVal;
    }
    return updatedVariables;
  };

  // chart query variables
  // const buildVariables2 = () => {
  //   const start = formatDateYYYYMMDD(startDate);
  //   const end = formatDateYYYYMMDD(endDate);
  //   const updatedVariables = {
  //     operatorId: operatorId,
  //     dateStart: start,
  //     dateEnd: end,
  //     selectedRadio: selectedRadio,
  //   };
  //   if (selectedLockerName.trim() !== "-1" && selectedLockerType !== "-1") {
  //     if (selectedLockerName != "200") {
  //       updatedVariables.selectedLockerName = parseInt(selectedLockerName);
  //     }
  //   }
  //   if (selectedPartnerCompany.trim() !== "-1") {
  //     updatedVariables.selectedPartnerCompany = parseInt(
  //       selectedPartnerCompany
  //     );
  //   }
  //   if (
  //     selectedPartnerBranch.trim() !== "-1" &&
  //     selectedLockerName.trim() !== "-1"
  //   ) {
  //     updatedVariables.selectedPartnerBranch = parseInt(selectedPartnerBranch);
  //   }
  //   console.log(updatedVariables);
  //   return updatedVariables;
  // };

  const [totalCol, setTotalCol] = useState([]);
  const handleTabClick = (tabValue) => {
    // setSelectedRadio(tabValue);
    setFilterVariables({
      ...filterVariables,
      selectedRadio: tabValue,
    });
  };

  useEffect(() => {
    const ChartVariable = buildVariables(true);
    fetchChartData({
      variables: ChartVariable,
    });
  }, [filterVariables.selectedRadio]);

  useEffect(() => {
    // if (filterVariables.selectedPartnerBranch.length !== 0) {
    const ChartVariable = buildVariables(true);
    fetchChartDataByBranch({
      variables: ChartVariable,
    });
    // }
  }, [filterVariables.selectedRadio]);

  // useEffect(() => {
  //   if (
  //     selectedLockerName !== "-1" &&
  //     selectedPartnerCompany !== "-1" &&
  //     startDate &&
  //     endDate
  //   ) {
  //     setShowTable(true);
  //   } else {
  //     setShowTable(false);
  //   }
  // }, [selectedLockerName, selectedPartnerCompany, startDate, endDate]);

  function EmptyCriteriaHandler() {
    const TableVariables = buildVariables(false);
    const ChartVariable = buildVariables(true);
    if (selectedLockerType != "-1" && selectedLockerName == "-1") {
      setPartnerPerformnaceReports([]);
      setNoResultSearch(true);
      setIsLoading(false);
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: "Invalid Locker",
        message: `Please select a locker name.`,
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => setValidation({ open: false })}
              value={"OK"}
              className={"p-2 w-full"}
            />
          </div>
        ),
      });
    } else if (selectedLockerType != "-1" && lockerName.length === 1) {
      setIsLoading(false);
      setIsTableEmpty(true);
      // setValidation({
      // 	icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      // 	title: "Invalid Locker",
      // 	message: `No lockers was found.`,
      // 	open: true,
      // 	actions: (
      // 		<div className="w-full">
      // 			<Button
      // 				onClick={() => setValidation({ open: false })}
      // 				value={"OK"}
      // 				className={"p-2 w-full"}
      // 			/>
      // 		</div>
      // 	),
      // });
    } else if (selectedPartnerCompany != "-1" && partnerBranch.length === 0) {
      setPartnerPerformnaceReports([]);
      setIsLoading(false);
      setNoResultSearch(true);
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: "Invalid Partner Branch",
        message: `Please select a partner branch`,
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => setValidation({ open: false })}
              value={"OK"}
              className={"p-2 w-full"}
            />
          </div>
        ),
      });
    } else {
      fetchChartData({
        variables: ChartVariable,
      });
      fetchChartDataByBranch({
        variables: ChartVariable,
      });
      QUERYpartnerPerformance({
        variables: TableVariables,
      });

      setNoResultSearch(false);
      setIsTableEmpty(false);
    }
  }

  // This function is for handling search data
  const handleSearch = (event, data) => {
    setIsLoading(true);
    setPartnerPerformnaceReports([]);
    handleSearchResultHeader();

    setFilterVariables({
      ...filterVariables,
      selectedRadio: "weekday",
    });

    if (!validateFilters()) {
      return;
    }

    EmptyCriteriaHandler();
  };

  const validateFilters = () => {
    const {
      lockerType,
      selectedLockerName,
      selectedPartnerCompany,
      selectedPartnerBranch,
      startDate,
      endDate,
    } = filterVariables;

    // Locker validation
    if (lockerType !== "-1" && selectedLockerName.length === 0) {
      showValidationModal("Invalid Locker", "Please select a locker name.");
      return false;
    }

    // Partner branch validation
    if (selectedPartnerCompany !== "-1" && selectedPartnerBranch.length === 0) {
      showValidationModal(
        "Invalid Partner Branch",
        "Please select a partner branch."
      );
      return false;
    }

    // Date validation
    if (!startDate && !endDate) {
      showValidationModal("Invalid Date", "Please select a date range.");
      return false;
    }

    if (!startDate) {
      showValidationModal("Invalid Date", "No start date selected.");
      return false;
    }

    if (!endDate) {
      showValidationModal("Invalid Date", "No end date selected.");
      return false;
    }

    if (startDate > endDate) {
      showValidationModal(
        "Invalid Date",
        "Start date must be before end date."
      );
      return false;
    }

    return true;
  };
  const showValidationModal = (title, message) => {
    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      title,
      message,
      open: true,
      actions: (
        <div className="w-full">
          <Button
            onClick={() => setValidation({ open: false })}
            value={"OK"}
            className={"p-2 w-full"}
          />
        </div>
      ),
    });
    setIsLoading(false);
  };

  return (
    <ContentContainer containerTitle="Partner Performance Report">
      <h2 className={"font-bold"}>Locker</h2>
      <div className={"grid grid-cols-6 w-full gap-y-2 pb-2"}>
        <p className="col-span-1">Locker Type:</p>
        <Select
          items={lockerType}
          value={filterVariables.lockerType}
          defaultOption={{ value: "-1", name: "-- ALL --" }}
          onChange={(event) => {
            handleFilterChange(event, "lockerType");
          }}
          className={"col-span-3"}
        />
        <div className="col-span-2"></div>

        <p className="col-span-1">Locker Name:</p>
        <MultiSelect
          items={lockerName}
          className={"col-span-3"}
          value={filterVariables.selectedLockerName}
          placeholder={`${
            filterVariables.lockerType === "-1" ? "ALL Lockers" : "Select..."
          }`}
          disabled={filterVariables.lockerType === "-1"}
          onChange={(event) => {
            handleFilterChange(event, "selectedLockerName");
          }}
        />
      </div>

      <h2 className={"font-bold"}>Partner</h2>
      <div className={"grid grid-cols-6 w-full gap-y-2 pb-2"}>
        <p className="col-span-1">Partner Company:</p>
        <Select
          items={partnerCompany}
          className={"col-span-3"}
          value={filterVariables.selectedPartnerCompany}
          defaultOption={{ value: "-1", name: "-- ALL--" }}
          onChange={(event) => {
            handleFilterChange(event, "selectedPartnerCompany");
          }}
        />
        <div className="col-span-2"></div>

        <p className="col-span-1">Partner Branch:</p>
        <MultiSelect
          items={partnerBranch}
          className={"col-span-3"}
          value={filterVariables.selectedPartnerBranch}
          placeholder={`${
            filterVariables.selectedPartnerCompany === "-1"
              ? "ALL Partner Branches"
              : "Select..."
          }`}
          disabled={
            filterVariables.selectedPartnerCompany !== "-1" ? false : true
          }
          onChange={(event) => {
            handleFilterChange(event, "selectedPartnerBranch");
          }}
        />
      </div>
      <h2 className={"font-bold"}>Date range:</h2>

      {/* <DateRangePicker
				className={"w-1/2"}
				startDate={startDate}
				endDate={endDate}
				onChange={(event) => {
					if (event.target.name === "signupDateFrom") {
						setStartDate(event.target.value);
					} else {
						setEndDate(event.target.value);
					}
				}}
			/> */}

      <PrimereactDateRange
        className={"w-1/2"}
        startDate={filterVariables.startDate}
        endDate={filterVariables.endDate}
        startDateName="startDate"
        endDateName="endDate"
        timeOnly={false}
        onChange={(event, fieldName) => {
          handleFilterChange(event, fieldName);
        }}
        readOnlyInput={true}
        max={new Date()} // Today's date as the maximum
        min={new Date("2021-01-01")} // Jan 1, 2021, as the minimum
      />
      <div className="grid grid-cols-2 w-1/2 gap-y-2">
        <div></div>
        <u
          className="flex hover:text-blue-500 cursor-pointer mt-4 ml-auto w-min"
          onClick={() => {
            setFilterVariables({
              ...filterVariables,
              startDate: maxDate,
              endDate: new Date(),
              selectedLockerName: [],
              lockerType: "-1",
              selectedStatus: "-1",
              selectedPartnerCompany: "-1",
              selectedPartnerBranch: [],
            });
          }}
        >
          <i className="fas fa-undo my-auto"></i>
          <p className="ml-1">Reset</p>
        </u>
      </div>

      <Button
        value={"Search"}
        onClick={handleSearch}
        className={"w-40 h-8 m-5"}
      />

      {isLoading && <LoadingComponent />}
      {partnerPerfromanceReports.length > 0 && noResultSearch == false && (
        <div className="px-4">
          <Card>
            <div id="Partner Performance Report">
              <Accordion multiple activeIndex={[0, 1]} className="w-full">
                {/* 1. Partner Performance Report (Chart + Table) */}
                <AccordionTab header="Partner Performance Report">
                  {/* Chart View */}
                  <DownloadButton
                    id="Partner Performance Report - Chart"
                    pngOnly={true}
                  />
                  <div className={"py-2 border-black border bg-yellow-400"}>
                    <div className={"grid grid-row-2 bg-yellow-400 p-2 "}>
                      <div>
                        <TabComponent
                          tabs={[
                            {
                              value: "daily",
                              title: "Daily",
                            },
                            {
                              value: "weekly",
                              title: "Weekly",
                            },
                            {
                              value: "weekday",
                              title: "Day of the Week",
                            },
                            {
                              value: "monthly",
                              title: "Monthly",
                            },
                            {
                              value: "yearly",
                              title: "Yearly",
                            },
                          ]}
                          onTabClick={handleTabClick}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={"border border-black mb-4"}
                    id="Partner Performance Report - Chart"
                  >
                    <div className={"grid grid-row-2 bg-yellow-400 p-2 "}>
                      <div className={"text-center"}>
                        <span>
                          <h1 className={"font-bold"}>
                            PARTNER PERFORMANCE REPORT
                          </h1>
                        </span>
                        <span>
                          <h1 className={"font-bold"}>{searchResultHeader}</h1>
                        </span>
                      </div>
                    </div>

                    <ResponsiveContainer width={"100%"} height={500}>
                      <BarChart
                        data={chartData}
                        margin={{
                          top: 5,
                          right: 30,
                          left: 20,
                          bottom: 30,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis>
                          <Label
                            value={"Packages"}
                            angle={-90}
                            position={"insideLeft"}
                          />
                        </YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="top"
                          formatter={(value) =>
                            `${value}: ${
                              lockerForChart[value.toString()] == 1
                                ? `${lockerForChart[value.toString()]} Locker`
                                : `${lockerForChart[value.toString()]} Lockers`
                            }, ${
                              packageForChart[value.toString()] == 1
                                ? `${packageForChart[value.toString()]} Package`
                                : `${
                                    packageForChart[value.toString()]
                                  } Packages`
                            }`
                          }
                          layout="vertical"
                        />
                        {partnerCompanyForChart}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Table View */}

                  <div className="pt-5 pb-10">
                    {!showTable ? (
                      <GroupedTable
                        groupBy={{
                          header_name: "Partner Name",
                          key: "partnerName",
                        }}
                        multiData={["locker", "packages"]}
                        totalData={totalCol}
                        headers={[
                          {
                            header_name: "Locker Name",
                            key: "locker",
                          },
                          {
                            header_name: "Packages",
                            key: "packages",
                          },
                        ]}
                        items={partnerPerfromanceReports}
                      />
                    ) : (
                      <ParterGroupedTable
                        groupBy={{
                          header_name: "Partner Name",
                          key: "partnerName",
                        }}
                        multiData={["locker", "packages"]}
                        totalData={totalCol}
                        headers={[
                          {
                            header_name: "Locker Name",
                            key: "locker",
                          },
                          {
                            header_name: "Packages",
                            key: "packages",
                          },
                        ]}
                        items={partnerPerfromanceReports}
                      />
                    )}
                  </div>
                </AccordionTab>

                {/* 2. Partner Performance By Branch Report */}
                <AccordionTab header="Partner Performance By Branch Report">
                  <DownloadButton
                    id="Partner Performance By Branch Report - Chart"
                    pngOnly={true}
                  />
                  <div className={"py-2 border-black border bg-yellow-400"}>
                    <div className={"grid grid-row-2 bg-yellow-400 p-2 "}>
                      <div>
                        <TabComponent
                          tabs={[
                            {
                              value: "daily",
                              title: "Daily",
                            },
                            {
                              value: "weekly",
                              title: "Weekly",
                            },
                            {
                              value: "weekday",
                              title: "Day of the Week",
                            },
                            {
                              value: "monthly",
                              title: "Monthly",
                            },
                            {
                              value: "yearly",
                              title: "Yearly",
                            },
                          ]}
                          onTabClick={handleTabClick}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={"border border-black"}
                    id="Partner Performance By Branch Report - Chart"
                  >
                    <div className={"grid grid-row-2 bg-yellow-400 p-2 "}>
                      <div className={"text-center"}>
                        <span>
                          <h1 className={"font-bold"}>
                            PARTNER PERFORMANCE BY BRANCH REPORT
                          </h1>
                        </span>
                        <span>
                          <h1 className={"font-bold"}>{searchResultHeader}</h1>
                        </span>
                      </div>
                    </div>

                    <ResponsiveContainer width={"100%"} height={500}>
                      <BarChart
                        data={branchChartData}
                        margin={{
                          top: 5,
                          right: 30,
                          left: 20,
                          bottom: 30,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis>
                          <Label
                            value={"Packages"}
                            angle={-90}
                            position={"insideLeft"}
                          />
                        </YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="top"
                          formatter={(value) =>
                            `${value}: ${
                              branchForChart[value.toString()] == 1
                                ? `${branchForChart[value.toString()]} Package`
                                : `${branchForChart[value.toString()]} Packages`
                            }`
                          }
                          layout="vertical"
                        />
                        {partnerBranchForChart}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </AccordionTab>
              </Accordion>
            </div>
          </Card>
        </div>
      )}

      {isTableEmpty ? <NoDataFoundTemplate /> : ""}
      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={validation.actions}
      />
    </ContentContainer>
  );
};

export default PartnerPerformanceReports;
